import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { FaSave, FaSearch } from 'react-icons/fa';
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { TrashIcon } from '@heroicons/react/24/outline';
import { PiDotsThree } from 'react-icons/pi';
import { obscureString } from '../utils/obsureString';
import { formatDateTime } from '../utils/formatDate';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination';

const UnknownPackages = () => {
  const [packages, setPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalData, setModalData] = useState(null); // Modal for claiming
  const [deleteId, setDeleteId] = useState(null); // Delete confirmation
  const [enteredTrackingNumber, setEnteredTrackingNumber] = useState(''); // User input for claim
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [metaData, setMetaData] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
  });

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const user = JSON.parse(localStorage.getItem('user'));

  // Fetch packages from the API
  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/unclaim?page=${currentPage}&search=${searchTerm}`);
      if (!response.ok) throw new Error('Failed to fetch packages');
      const data = await response.json();

      setPackages(data.data || []);
      setMetaData(data.meta);
    } catch (err) {
      toast.error(err.message || 'Error fetching packages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    // Reset to page 1 when searching
  }, [currentPage]);

  // Handle searching with debounce effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchPackages();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Handle Claim Submission
  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!modalData || enteredTrackingNumber.trim() === '') return;

    if (enteredTrackingNumber === modalData.tracking_number) {
      try {
        const response = await fetch(`${apiUrl}/api/packages/claim/${modalData.tracking_number}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ courierId: user?.courier_id }),
        });
        if (!response.ok) throw new Error('Failed to claim the package');
        toast.success('Package claimed successfully!');
        await fetchPackages();
        setModalData(null);
        setEnteredTrackingNumber('');
      } catch (err) {
        toast.error(err.message || 'Error claiming the package.');
      }
    } else {
      toast.error('Entered tracking number does not match. Please try again.');
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/unknown-packages/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete the package');
      toast.success('Package deleted successfully!');
      await fetchPackages();
      setDeleteId(null);
    } catch (err) {
      toast.error(err.message || 'Error deleting the package.');
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < metaData.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <Layout>
      <div className="p-8 bg-white min-h-full">
        {/* Header Section */}
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Unknown Packages</h2>

            {/* Top Pagination Control */}
            <div className="flex items-center gap-4 bg-gray-50 p-1.5 rounded-lg border border-gray-100">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="p-1 hover:bg-white hover:shadow-sm rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <BiChevronLeft size={24} className="text-gray-600" />
              </button>
              <span className="text-sm font-medium text-gray-600 px-2">
                Page {metaData.currentPage} of {metaData.totalPages || 1}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= metaData.totalPages}
                className="p-1 hover:bg-white hover:shadow-sm rounded transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <BiChevronRight size={24} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by tracking number, consignee, description..."
              className="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader />
          </div>
        ) : (
          <div className="overflow-hidden border border-gray-100 rounded-xl shadow-sm bg-white">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Tracking #</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Air Way Bill</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Delivery Service</th>
                  <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {packages.length > 0 ? (
                  packages.map((pkg) => (
                    <tr key={pkg.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="px-6 py-5">
                        <span className="text-sm font-medium text-gray-700 font-mono">
                          {obscureString(pkg.tracking_number || '')}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-sm text-gray-600">
                          {pkg.airWayBill || '—'}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {pkg.deliveryService?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={() => setModalData(pkg)}
                            className="bg-[#FF6D00] hover:bg-[#E65100] text-white px-6 py-2 rounded-lg text-sm font-bold transition-all shadow-sm hover:shadow active:scale-95"
                          >
                            Assign
                          </button>
                          {user?.role === 'super-admin' && (
                            <button
                              onClick={() => setDeleteId(pkg.id)}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-gray-500 text-sm">No packages available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Bottom Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
              <Pagination
                currentPage={metaData.currentPage}
                totalPages={metaData.totalPages}
                onPageChange={(page) => setCurrentPage(page)}
                itemsPerPage={metaData.itemsPerPage}
                totalItems={metaData.totalItems}
              />
            </div>
          </div>
        )}
      </div>

      {/* Claim/Assign Modal */}
      {modalData && (
        <dialog className="modal modal-open">
          <div className="modal-box p-8 max-w-md rounded-2xl shadow-2xl border border-gray-100">
            <div className="flex flex-col items-start gap-1 mb-6">
              <h3 className="font-bold text-2xl text-gray-800">Assign Package</h3>
              <p className="text-sm text-gray-500">
                To confirm this is your package, please enter the full tracking number.
              </p>
            </div>

            <form onSubmit={handleClaimSubmit}>
              <div className="form-control w-full gap-2">
                <label className="text-sm font-bold text-gray-700 ml-1">
                  Full Tracking Number
                </label>
                <input
                  type="text"
                  placeholder="Paste tracking number here..."
                  className="input input-bordered w-full h-12 rounded-xl focus:ring-2 focus:ring-blue-500/20"
                  value={enteredTrackingNumber}
                  onChange={(e) => setEnteredTrackingNumber(e.target.value)}
                  required
                />
              </div>

              <div className="modal-action gap-3 mt-8">
                <button
                  type="button"
                  className="btn bg-gray-100 hover:bg-gray-200 border-none text-gray-700 rounded-xl px-6 h-12 normal-case font-bold"
                  onClick={() => {
                    setModalData(null);
                    setEnteredTrackingNumber('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn bg-[#002e63] hover:bg-gray-800 border-none text-white rounded-xl px-8 h-12 normal-case font-bold shadow-lg"
                >
                  <FaSave /> Confirm Assignment
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop bg-gray-900/40 backdrop-blur-sm" onClick={() => setModalData(null)}>
            <button>close</button>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box p-8 max-w-sm rounded-2xl shadow-2xl border border-gray-100 text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Deletion</h3>
            <p className="text-gray-500 text-sm mb-8">This action cannot be undone. Are you sure you want to delete this unknown package?</p>
            <div className="flex gap-3 justify-center">
              <button
                className="btn bg-gray-100 hover:bg-gray-200 border-none text-gray-700 rounded-xl px-6 h-12 font-bold"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="btn bg-red-600 hover:bg-red-700 border-none text-white rounded-xl px-6 h-12 font-bold shadow-lg shadow-red-500/20"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop bg-gray-900/40 backdrop-blur-sm" onClick={() => setDeleteId(null)}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </Layout>
  );
};

export default UnknownPackages;
